import { Modal } from '@/components/common/Modal'
import RequisitionFormView from '@/components/inProgressOrder/RequisitionFormView'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'
import type { OrderFormInfoData, RequisitionFormData } from '@/types/RequisitionFormTypes'

interface OrderFormViewModalProps {
  isOpen: boolean
  onClose: () => void
  formData: RequisitionFormData | null
  orderData: OrderFormInfoData | null
  canonical?: CanonicalOrderPayload | null
  /**
   * Placed order id. When set, the TRF is fetched from the artifact provider-api stored at
   * submit rather than re-rendered in the browser (GEN-4161).
   */
  orderId?: string | null
  /** Audit origin for the order-details read behind the pre-signed URL. */
  caller?: string
  /**
   * When true (and an `orderId` is set), the TRF is re-rendered server-side from the current
   * `canonical` rather than fetched from the stored submit-time artifact — so the in-progress
   * viewer reflects unsent edits. Forwarded to `RequisitionFormView`.
   */
  regenerateFromCanonical?: boolean
  /** Bumped by the caller (e.g. each open) to re-fetch the regenerated TRF. */
  regenerateRefreshToken?: string | number | null
}

export function OrderFormViewModal({
  isOpen,
  onClose,
  formData,
  orderData,
  canonical,
  orderId,
  caller,
  regenerateFromCanonical,
  regenerateRefreshToken,
}: OrderFormViewModalProps) {
  // A stored TRF needs neither canonical nor header data — it is already a rendered document —
  // so an order id alone is enough content to show the viewer.
  const hasContent = Boolean(orderId) || ((canonical?.order?.selectedOption || formData) && orderData)
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Requisition Form" size="xl">
      <div className="bg-background-quaternary px-6 pb-6 pt-4 dark:bg-gray-800">
        {hasContent ? (
          <div className="mx-auto max-w-[820px] rounded-base bg-white shadow-lg dark:bg-gray-900">
            <RequisitionFormView
              canonical={canonical}
              formData={formData}
              orderData={orderData}
              orderId={orderId}
              trfFetchCaller={caller}
              regenerateFromCanonical={regenerateFromCanonical}
              regenerateRefreshToken={regenerateRefreshToken}
              showHeader={false}
              viewerClassName="h-[calc(100vh-520px)]"
            />
          </div>
        ) : (
          <div className="text-sm text-text-body dark:text-gray-400">Requisition form is unavailable.</div>
        )}
      </div>
    </Modal>
  )
}
